<main id="content" tabindex="-1" class="agent-chat">
    @if(canViewAgent()){
    <div class="visually-hidden" role="status" aria-live="polite">{{ liveStatus }}</div>

    <div class="bg-paws-header border-bottom content-header d-flex align-items-center gap-3">
        <div class="row flex-grow-1 align-items-center">
            <h1 class="col-12 col-md spe-3 mb-0">PAWS Digital Agent</h1>
            <div class="mt-2 mt-md-0 col-12 col-md-auto">
            @if ( currentDefinition() !== null ) {
                <span class="agent-pill px-3 py-1 rounded-pill fw-semibold" [class.it]="currentDefinition()!.accent === 'it'"
                    [class.student]="currentDefinition()!.accent === 'student'">
                    <span class="dot"></span>{{ currentDefinition()!.label }}
                </span>
            } @else if ( currentAgent() !== null ) {
                <!-- A locked agent with no catalogue entry, e.g. a paws_agent thread. -->
                <span class="agent-pill px-3 py-1 rounded-pill fw-semibold"><span class="dot"></span>{{ currentAgentLabel() }}</span>
            } @else {
                <span class="agent-pill px-3 py-1 rounded-pill fw-semibold"><span class="dot"></span>No agent selected</span>
            }
            </div>
        </div>
        

    </div>

    <div class="shell" [class.side-open]="sideOpen" [class.side-collapsed]="sideCollapsed">
        <app-conversation-sidebar id="conversationSidebar" class="conversation-list"
                                  [groupedRows]="groupedRows"
                                  [activeId]="activeId"
                                  [query]="query"
                                  [listError]="listError"
                                  [openMenu]="openMenu"
                                  [pendingDeleteId]="pendingDeleteId"
                                  [renamingId]="renamingId"
                                  [(renameText)]="renameText"
                                  (newChatRequested)="newChat()"
                                  (retryRequested)="loadConversations()"
                                  (queryChange)="query = $event; onQueryChange()"
                                  (conversationOpened)="openConversation($event)"
                                  (menuToggled)="toggleMenu($event.historyId, $event.event)"
                                  (renameStarted)="startRename($event)"
                                  (renameCommitted)="commitRename()"
                                  (renameCancelled)="cancelRename()"
                                  (deleteRequested)="requestDelete($event)"
                                  (deleteConfirmed)="confirmDelete($event)"
                                  (menuDismissed)="closeMenu(false)"
                                  (openInNewChatRequested)="openInNewChat($event)"></app-conversation-sidebar>

        <div class="main">

            <div class="conv-head p-3">
                <button class="btn btn-sm btn-light collapse-side d-none d-lg-inline-flex" type="button"
                        (click)="sideCollapsed = !sideCollapsed"
                        aria-controls="conversationSidebar"
                        [attr.aria-expanded]="!sideCollapsed"
                        [title]="sideCollapsed ? 'Show conversations' : 'Hide conversations'"
                        [attr.aria-label]="sideCollapsed ? 'Show conversations' : 'Hide conversations'">
                    <i class="bi" [class.bi-layout-sidebar-inset]="!sideCollapsed"
                       [class.bi-layout-sidebar]="sideCollapsed " aria-hidden="true"></i>
                       {{ currentTitle() }}
                </button>

                <button class="btn btn-sm btn-light collapse-side d-inline-flex d-lg-none" type="button"
                        (click)="sideOpen = !sideOpen" aria-label="Conversations"
                        aria-controls="conversationSidebar" [attr.aria-expanded]="sideOpen">
                    <i class="bi" [class.bi-layout-sidebar-inset]="!sideOpen"
                       [class.bi-layout-sidebar]="sideOpen " aria-hidden="true"></i>
                       {{ currentTitle() }}
                </button>
            </div>

            <div class="thread-wrap" #threadWrap (scroll)="onThreadScroll()">
                <div class="thread">

                    @if ( activeId === null ) {
                        <div class="picker">
                            <h3 class="p-title" id="pickerTitle">What do you need help with?</h3>
                            <div class="p-sub mb-3">
                                Pick a service area — your question is routed to that agent, and this conversation stays with it.
                            </div>
                            <div class="tabs d-flex me-2 mb-2 flex-grow-1 mb-3" role="group" aria-labelledby="pickerTitle">
                                @for ( agent of agents; track agent.key ) {
                                    @if(hasAccessToAgent(agent.roles)){
                                    <button class="tab d-flex me-2 mb-2 px-3 py-3 btn btn-secondary text-start text-dark border border-dark" type="button"
                                            [attr.data-agent]="agent.accent"
                                            [attr.aria-pressed]="draftAgent === agent.key"
                                            (click)="pickAgent(agent.key)">
                                        <span class="icon"><i class="bi me-2" [class]="agent.icon" aria-hidden="true"></i></span>
                                        <span>
                                            <span class="t-title">{{ agent.label }}</span>
                                            @if ( agent.isNew ) { <span class="ms-2 badge rounded-pill bg-warning text-dark">NEW</span> }
                                            <span class="t-sub">{{ agent.blurb }}</span>
                                        </span>
                                    </button>
                                    }

                                }
                            </div>
                        </div>

                        @if ( currentDefinition() !== null ) {
                            <app-chat-turn [turnRole]="'assistant'"
                                           [accent]="currentDefinition()!.accent"
                                           [agentName]="currentDefinition()!.label"
                                           [greetingName]="displayName"
                                           [content]="currentDefinition()!.welcome"></app-chat-turn>
                        }
                    }

                    @if ( loadingThread && turns.length === 0 ) {
                        <div class="thread-loading">
                            <div class="spinner-border" role="status">
                                <span class="visually-hidden">Loading conversation…</span>
                            </div>
                        </div>
                    }

                    @for ( turn of turns; track $index ) {
                        <app-chat-turn [turnRole]="turn.role"
                                       [content]="turn.content"
                                       [sources]="turn.sources"
                                       [streaming]="turn.streaming"
                                       [failed]="turn.failed"
                                       [incomplete]="turn.incomplete"
                                       [rating]="turn.rating"
                                       [turnId]="turn.id"
                                       [feedbackError]="feedbackErrorFor(turn.id)"
                                       [feedbackSending]="isFeedbackSending(turn.id)"
                                       (feedbackEmitter)="sendFeedback($event)"
                                       [agentName]="currentAgent() !== null ? currentAgentLabel() : null"></app-chat-turn>
                    }

                    <!-- Announced via the status region above, so not role="alert" here. -->
                    @if ( loadError !== "" ) {
                        <div class="alert alert-warning" role="alert">{{ loadError }}</div>
                    }
                    @if ( streamError !== "" ) {
                        <div class="alert alert-warning" role="alert">{{ streamError }}</div>
                    }
                    @if ( deleteError !== "" ) {
                        <div class="alert alert-warning" role="alert">{{ deleteError }}</div>
                    }
                </div>
            </div>

            <div class="composer-wrap">
                <div class="composer" #composer>
                    <!-- Maybe we can make this form group but I do not like how it looks -->
                    <label class="visually-hidden" for="agentChatInput">Your message</label>
                    <textarea id="agentChatInput" #composerInput class="form-control" rows="1"
                              [placeholder]="placeholder()"
                              [disabled]="currentAgent() === null || !accessToChatToAgent()"
                              autocomplete="off"
                              [attr.aria-describedby]="currentAgent() !== null ? 'agentChatCount' : null"
                              [(ngModel)]="userInput"
                              (input)="autoGrowComposer()"
                              (keydown.enter)="onComposerEnter($event)"></textarea>
                    <button class="btn send" type="button" [disabled]="!canSend()" (click)="send()"
                            title="Send" aria-label="Send message">
                        <i class="bi bi-send-fill" aria-hidden="true"></i>
                    </button>
                    <!-- Only reachable while an answer is streaming. -->
                    @if ( pending ) {
                        <button class="btn stop" type="button" (click)="stopStreaming()"
                                title="Stop" aria-label="Stop generating">
                            <i class="bi bi-stop-fill" aria-hidden="true"></i>
                        </button>
                    }
                </div>

                @if ( currentAgent() !== null ) {
                    <div class="hint" id="agentChatCount" [class]="{
                        'text-danger':isSoftLimitExceeded,
                        'fw-bold':isSoftLimitExceeded
                    }">
                        {{userInputLength}}/{{maxLength}}
                        <!-- Always rendered so the over-limit message is announced when it appears. -->
                        <span class="limit-warning" aria-live="polite">@if(isSoftLimitExceeded){ - Exceeded character limit. Please reduce message to {{maxLength}} characters or less. }</span>
                    </div>
                    
                }

                <div id="disclaimerWrapper" #disclaimerWrapper class="mt-2">
                    <button class="btn btn-primary btn-sm float-end" type="button"
                            [attr.aria-expanded]="showDisclaimerContent"
                            aria-controls="disclaimerContent"
                            (click)="toggleDisclaimer()">PAWS Agent Disclaimer</button>
                    <div id="disclaimerContent" #disclaimerContent [class.on]="showDisclaimerContent">
                        <h5>PAWS Agent Disclaimer</h5>
                        <p>
                            This digital agent uses generative AI to provide responses based on internal knowledge
                            repositories, including Service Management knowledge bases. While we aim for accuracy,
                            AI-generated content may occasionally be incorrect, incomplete, or misleading
                            ("hallucinations"). Please verify responses, especially if something seems unclear or
                            unexpected.
                        </p>
                        <p><b>Important:</b></p>
                        <ul>
                            <li>This agent does <b>not</b> provide legally binding advice.</li>
                            <li>For <b>high-impact matters</b> such as system outages, security incidents, compliance
                                or financial issues, or decisions affecting academic or research concerns, always
                                consult official documentation or appropriate personnel.</li>
                            <li>Responses are based on point-in-time data and may not reflect the most current
                                policies, procedures, or system changes.</li>
                            <li>User interactions may be logged to improve quality.</li>
                            <li><b>Do not share sensitive personal or confidential information</b> during interactions.</li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    </div>

    @if ( sideOpen ) {
        <div class="scrim" aria-hidden="true" (click)="sideOpen = false; closeMenu()"></div>
    }
    }
    
    @else{
        <div class="backgroundMask text-center">
            <div class="alert alert-info" role="alert">
                <div class="h2">Access Denied</div>
                <div class="mt-3 h5">You do not currently have permission to see this page. </div>
                <div class="mt-3 h5">If you do, please sign out and try again.</div>
            </div>
        </div>
    }

</main>
